import { useEffect, createElement } from 'react'

import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { Pin, IconProps } from '@/components/Icons'
import { LanguageSelector } from '@/features/Footer/LanguageSelector'
import { NavLink } from '@/features/header/Header'
import { useGameSlug } from '@/features/storefronts/game-provider'
import { LOGIN_STATUS } from '@/features/user/userSlice'

import { useMarketContext } from '../../../../../contexts/MarketContext'
import { CategoryLinks } from '../CategoryLinks'
import { useMenu } from './menu-provider'
import MenuContainer from './MenuContainer'
import { MenuUser } from './MenuUser'

import styles from './menu.module.scss'

type MenuProps = {
  infoLinks: Array<NavLink & { icon: (props: IconProps) => JSX.Element }>
  loginStatus: LOGIN_STATUS
  categoryLinks: NavLink[]
  isDesktop: boolean
  isProductPage: boolean
}

export const Menu = ({ infoLinks, loginStatus, categoryLinks, isDesktop, isProductPage }: MenuProps) => {
  const router = useRouter()
  const gameSlug = useGameSlug()
  const { isOpen, setIsOpen } = useMenu()

  const { countryCode } = useMarketContext('Menu')
  const countries = useTranslations('Countries')
  const countryName = countryCode ? countries(countryCode.toLowerCase() as keyof IntlMessages['Countries']) : ''

  useEffect(() => {
    if (!router.events) return
    const listener = () => setIsOpen(false)
    router.events.on('routeChangeComplete', listener)
    return () => router.events.off('routeChangeComplete', listener)
  }, [router.events, setIsOpen])

  const closeMenu = () => {
    setIsOpen(false)
  }

  return (
    <MenuContainer isOpen={isOpen} closeMenu={closeMenu}>
      <div className={styles.content}>
        {!isDesktop && !isProductPage && <CategoryLinks categories={categoryLinks} />}

        <div className={cx(styles.menuLinks, styles[gameSlug])}>
          <MenuUser loginStatus={loginStatus} />

          <ul className={styles.list} data-cy="menu-links">
            {infoLinks.map(({ icon, href, label, target }) => (
              <li key={href}>
                <Link href={href} className={styles.navLink} target={target}>
                  <div>
                    {createElement(icon)}
                    <span>{label}</span>
                  </div>
                  <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L6 6L1 11" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </li>
            ))}
          </ul>

          <div className={styles.languageCountry}>
            <LanguageSelector id="menu-language-selector" direction="Down" isMobileNav storefront />
            <div className={styles.country}>
              <Pin width={16} />
              <div>{countryName}</div>
            </div>
          </div>
        </div>
      </div>
    </MenuContainer>
  )
}

Menu.displayName = 'Menu'
